/**
 * Privacy banner — shown once, until the visitor decides.
 *
 * Clinical-minimal: white surface, generous spacing, Poppins, restrained
 * TruWe blue. Compact on mobile and anchored to the bottom so it never sits
 * over the buy button, the cart or the product imagery.
 *
 * Three equal-weight actions: Accept all, Reject optional, Choose settings.
 * Nothing optional is preselected and rejecting is a single tap.
 */
import { useEffect, useState } from "react";

import {
  PRIVACY_ACTIONS,
  PRIVACY_BANNER_BODY,
  PRIVACY_BANNER_TITLE,
} from "@/lib/consent/categories";
import {
  acceptAll,
  hasDecided,
  onConsentStateChange,
  rejectOptional,
} from "@/lib/consent/store";
import { logCategoryChoice } from "@/lib/consent/log";
import { PrivacySettingsDialog } from "./PrivacySettingsDialog";

export function PrivacyBanner() {
  const [show, setShow] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    setShow(!hasDecided());
    return onConsentStateChange((state) => setShow(!hasDecided(state)));
  }, []);

  const choose = (all: boolean) => {
    const state = all ? acceptAll() : rejectOptional();
    void logCategoryChoice(state);
    setShow(false);
  };

  return (
    <>
      <PrivacySettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
      {show && !settingsOpen ? (
        <div
          role="dialog"
          aria-label="Privacy preferences"
          className="fixed inset-x-0 bottom-0 z-[70] px-3 pb-3 sm:px-4 sm:pb-4"
        >
          <div className="mx-auto w-full max-w-3xl rounded-2xl border border-foreground/20 bg-foreground p-4 text-background shadow-xl sm:p-5">
            <p className="font-display text-base font-semibold sm:text-lg">
              {PRIVACY_BANNER_TITLE}
            </p>
            <p className="mt-1.5 text-xs leading-relaxed text-background/70 sm:text-sm">
              {PRIVACY_BANNER_BODY}
            </p>
            <div className="mt-3 flex items-center gap-2 sm:mt-4">
              <button
                type="button"
                onClick={() => choose(true)}
                className="h-9 flex-1 rounded-lg bg-background px-3 text-xs font-semibold text-foreground transition-opacity hover:opacity-90 sm:h-10 sm:flex-none sm:min-w-[9rem] sm:text-sm"
              >
                {PRIVACY_ACTIONS.acceptAll}
              </button>
              <button
                type="button"
                onClick={() => choose(false)}
                className="h-9 flex-1 rounded-lg border border-background px-3 text-xs font-semibold text-background transition-colors hover:bg-background/10 sm:h-10 sm:flex-none sm:min-w-[9rem] sm:text-sm"
              >
                {PRIVACY_ACTIONS.rejectOptional}
              </button>
              <button
                type="button"
                onClick={() => setSettingsOpen(true)}
                className="ml-auto h-9 px-2 text-xs font-medium text-background underline underline-offset-4 hover:opacity-80 sm:h-10 sm:text-sm"
              >
                {PRIVACY_ACTIONS.chooseSettings}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
